// shared logic between MoveComined/mobile.vue & Disclaimer/desktop.vue
import { isUspsProd, isUspsProdInternal } from '@mgo/core/src/environment';
import { attachFieldErrors } from '@mgo/core/src/validations/stringValidator';
import {
  mapErrors,
  objectIsValid,
} from '@mgo/core/src/validations/utils';
import { object } from 'yup';
import { track } from '../../analytics/events';
import errorMessages from '../../assets/data/error_messages.json';
import pageView from '../../mixins/pageView';
import handleChoice from '../../utils/analytics';
import validators from './validations';
import { isPhoneOptionalAddress } from '../../utils/contactPhoneNumber';

/**
 * Maps a boolean field from api response, to a radio value
 * @param {Boolean} value
 * @param {String}
 */
const mapBooleanToRadios = (value) => (value ? 'YES' : 'NO');

export default {
  mixins: [pageView],
  props: {
    forwardTypeVal: {
      type: String,
      default: '',
      required: false,
    },
    phoneNumberVal: {
      type: String,
      default: '',
      required: false,
    },
    phoneTypeVal: {
      type: String,
      default: 'MOBILE',
    },
    optInMailReminderVal: {
      type: Boolean,
      default: false,
      required: false,
    },
    businessWholeCompanyVal: {
      type: String,
      default: 'YES',
      required: false,
    },
    startDateVal: {
      type: String,
      default: '',
      required: false,
    },
    stopDateVal: {
      type: String,
      default: '',
      required: false,
    },
    emailVal: {
      type: String,
      default: '',
      required: false,
    },
    emailConfirmVal: {
      type: String,
      default: null,
      required: false,
    },
    viewMode: {
      type: String,
      default: '',
      required: true,
    },
  },
  data() {
    return {
      privacyActFullContent: false,
      offersChoice: false,
      autofillChoice: false,
      fieldErrors: [],
      improvedFieldErrors: {},
      dirty: null,
      lazy: true,
      showBusinessAlias: false,
      medUpdated: false, // start date updated
      sdUpdated: false, // stop date updated

      // coa
      dcoa: false,
      billingAddressType: '',
      businessName: '',
      businessAliases: [],
      businessAlias1: '',
      businessAlias2: '',
      businessAlias3: '',
      businessAlias4: '',
      informedDelivery: false,
      forwardType: '',
      moverType: '',
      startDate: '',
      stopDate: '',

      // mover
      firstName: '',
      middleName: '',
      lastName: '',
      suffix: '',
      email: '',
      emailConfirm: '',
      phoneNumber: '',
      phoneType: 'MOBILE',
      moverUUID: '',

      // address
      oldStreetAddress: '',
      oldCity: '',
      oldState: '',
      oldZipCode: '',
      oldUrbanizationName: '',
      oldPoBoxNumber: '',
      oldAddressUUID: '',
      newStreetAddress: '',
      newCity: '',
      newState: '',
      newZipCode: '',
      newUrbanizationName: '',
      newPoBoxNumber: '',
      newPrivateMailbox: '',
      newAddressUUID: '',
      recentConstruction: false,
      overrideAms: false,

      // enhanced business addresses
      primaryAddress: false,
      reuseNew: false,
      reuseOld: false,

      // page view
      isChoice: false,
      isSimplify: false,
      isControl: false,
      isVerifyInfo: false,
      isVerifyEdit: false,

      // coaCheck modal
      coaCheckModal: false,


      // determines if the current COA is the primaryy
      primaryBusiness: true,

      // indicates if the current COA is not new but already persisted in db
      editMode: false,

      // current COA orderUuid - this should change if a new COA is selected for editing
      orderUuid: '',

      business: [], // stores all business COA with one being marked as primary
      busAddresses: [], // stores all business addresses
      optInMailReminder: false,
    };
  },
  watch: {
    forwardTypeVal: {
      handler(newValue) {
        this.forwardType = newValue;
      },
    },
    phoneNumberVal: {
      handler(newValue) {
        this.phoneNumber = newValue;
      },
    },
    phoneTypeVal: {
      handler(newValue) {
        this.phoneType = newValue;
      },
    },
    optInMailReminderVal: {
      handler(newValue) {
        this.optInMailReminder = newValue;
      },
    },
    startDateVal: {
      handler(newValue) {
        this.startDate = newValue;
      },
    },
    stopDateVal: {
      handler(newValue) {
        this.stopDate = newValue;
      },
    },
    businessWholeCompanyVal: {
      handler(newValue) {
        this.businessWholeCompany = newValue;
      },
    },
    emailVal: {
      handler(newValue) {
        this.email = newValue;
      },
    },
    emailConfirmVal: {
      handler(newValue) {
        this.emailConfirm = newValue ?? '';
      },
    },
    primaryBusiness: {
      handler(value) {
        this.$emit('primary', value);
      },
    },
    isMultipleBusinesses(value) {
      this.$emit('multiple-businesses', value);
    },
    moverType(currentValue) {
      if (currentValue !== 'BUSINESS') {
        this.showBusinessAlias = false;
      }
    },
    form(newValue, oldValue) {
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue) && this.dirty !== null) {
        const changedValue = Object.keys(newValue)
          // we want to trigger the first interaction event for truthy or true -> false values
          // resolves bug on privacy act statement toggle MGOD-4581
          .filter((key) => newValue[key] || newValue[key] === false)
          .find((key) => oldValue[key] !== newValue[key]);
        this.trackFirstEvent(changedValue, 'input');
        this.dirty = true;
      }
    },
    newAddressString(newValue, oldValue) {
      if (oldValue.length && newValue !== oldValue) {
        this.recentConstruction = false;
        this.overrideAms = false;
        this.newAddressError = '';
        this.newConstructionResetAttempts();
      }
    },
  },
  computed: {
    addressErrorMap: () => new Map(Object.entries(errorMessages)),
    showNonControlExperience: () => JSON.parse(sessionStorage.getItem('showNonControlExperience')),
    showChoiceExperience: () => JSON.parse(sessionStorage.getItem('showChoiceExperience')),
    flagAmpersendEnabled() {
      return this.$store.get('flagAmpersendEnabled');
    },
    enhancedBusinessAliasesEnabled() {
      return this.$store.get('config@enhancedBusinessAliasesEnabled');
    },
    enhancedBusinessFlowEnabled() {
      return this.$store.get('config@enhancedBusinessFlowEnabled');
    },
    enhancedBusinessAliasesIncrementCount() {
      return this.$store.get('config@enhancedBusinessAliasesIncrementCount');
    },
    enhancedBusinessAliasesInitialCount() {
      return this.$store.get('config@enhancedBusinessAliasesInitialCount');
    },
    enhancedBusinessAliasesMaxCount() {
      return this.$store.get('config@enhancedBusinessAliasesMaxCount');
    },
    twoFactorAuthEnabled() {
      return (this.moverType !== 'BUSINESS' && this.$store.get('config@twoFactorAuthEnabled'));
    },
    oldAddressPhoneOptional() {
      return isPhoneOptionalAddress({
        city: this.oldCity,
        state: this.oldState,
        zipCode: this.oldZipCode,
      });
    },
    newAddressPhoneOptional() {
      return isPhoneOptionalAddress({
        city: this.newCity,
        state: this.newState,
        zipCode: this.newZipCode,
      });
    },
    phoneNumberOptional() {
      return this.oldAddressPhoneOptional || this.newAddressPhoneOptional;
    },
    phoneNumLabel() {
      return this.twoFactorAuthEnabled ? 'Mobile Phone Number (US only)' : 'Phone Number (US only)';
    },
    highIntent() {
      return this.$store.get('highIntent') || false;
    },
    coaDisputesEnabled() {
      return this.$store.get('config@coaDisputesEnabled') || false;
    },
    coaUUID() {
      return this.$coa.coaUUID;
    },
    informedDeliveryEligible() {
      return (
        this.moverType
        && this.moverType !== 'BUSINESS'
        && this.forwardType === 'PERMANENT'
        && this.billingAddressType !== 'OTHER'
      );
    },
    form() {
      const { dirty, ...form } = this.$data;
      return JSON.parse(JSON.stringify(form));
    },
    newAddressString() {
      const {
        newStreetAddress,
        newCity,
        newState,
        newZipCode,
        newUrbanizationName,
        newPrivateMailbox,
        newPoBoxNumber,
      } = this;
      return [
        newStreetAddress,
        newCity,
        newState,
        newZipCode,
        newUrbanizationName,
        newPrivateMailbox,
        newPoBoxNumber,
      ].join('');
    },
    icoaAmount() {
      return this.$store.get('config@icoaAmount') || 125;
    },
    icoaAmountFormatted() {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(this.icoaAmount / 100);
    },
    isProduction() {
      return isUspsProd(window.location.href) || isUspsProdInternal(window.location.href);
    },
    isMultipleBusinesses() {
      return this.business.length >= 2;
    },
    rules() {
      return object().shape(validators);
    },
    errors() {
      const fieldsToValidate = this.isSimplify ? [
        'moverType',
        'oldZipCode',
        'oldCity',
        'oldState',
        'oldStreetAddress',
        'newZipCode',
        'newCity',
        'newState',
        'newStreetAddress',
      ]
        : [
          'moverType',
          'email',
          'phoneNumber',
          'phoneType',
          'forwardType',
          'oldZipCode',
          'oldCity',
          'oldState',
          'oldStreetAddress',
          'newZipCode',
          'newCity',
          'newState',
          'newStreetAddress',
          'startDate',
        ];

      if (!this.isSimplify) {
        if (this.$options.validateConfirmEmail || this.emailConfirmVal !== null) {
          fieldsToValidate.push('emailConfirm');
        }

        if (this.forwardType === 'TEMPORARY') fieldsToValidate.push('stopDate');

        if (this.flagBusinessAliasEnabled && this.moverType === 'BUSINESS'
        && !this.enhancedBusinessAliasesEnabled) {
          fieldsToValidate.push('businessAlias1');
          fieldsToValidate.push('businessAlias2');
          fieldsToValidate.push('businessAlias3');
          fieldsToValidate.push('businessAlias4');
        }
      }

      if (this.showOldPoBox) {
        fieldsToValidate.push('oldPoBoxNumber');
      }

      if (this.oldState === 'PR') {
        fieldsToValidate.push('oldUrbanizationName');
      }

      if (this.showNewPrivateMailbox) {
        fieldsToValidate.push('newPrivateMailbox');
      }

      if (this.showNewPoBox) {
        fieldsToValidate.push('newPoBoxNumber');
      }

      if (this.newState === 'PR') {
        fieldsToValidate.push('newUrbanizationName');
      }

      if (this.moverType === 'FAMILY') {
        fieldsToValidate.push('familySameLastName');
        fieldsToValidate.push('familyRemaining');
      }

      if (this.moverType === 'BUSINESS') {
        fieldsToValidate.push('businessWholeCompany');

        fieldsToValidate.push('businessName');

        if (this.flagBusinessAliasEnabled && !this.enhancedBusinessAliasesEnabled) {
          fieldsToValidate.push('businessAlias1');
          fieldsToValidate.push('businessAlias2');
          fieldsToValidate.push('businessAlias3');
          fieldsToValidate.push('businessAlias4');
        }
      }


      if (this.renderV3Challenge) {
        fieldsToValidate.push('recaptchaChallengeToken');
      }

      return { ...mapErrors(this, fieldsToValidate), ...this.improvedFieldErrors };
    },

    formIsValid() {
      return objectIsValid(this.errors);
    },
    flagBusinessAliasEnabled() {
      return this.$store.get('flagBusinessAliasEnabled');
    },
    handleNavigation() {
      if (this.isSimplify) {
        return 'contact-info';
      }
      if (this.forwardType === 'TEMPORARY') {
        return 'verify-info-read';
      }

      if (this.showChoiceExperience) {
        return 'additional-services';
      }
      return 'verify-info-read';
    },
  },
  async mounted() {
    this.optInMailReminder = JSON.parse(sessionStorage.getItem('optInMailReminder') ?? false);
    this.forwardType = this.forwardTypeVal;
    this.startDate = this.startDateVal;
    this.stopDate = this.stopDateVal ? this.stopDateVal : this.startDateVal;
    this.businessWholeCompany = this.businessWholeCompanyVal;
    this.email = this.emailVal;
    this.emailConfirm = this.emailConfirmVal ?? '';
    this.phoneNumber = this.phoneNumberVal;

    this.setViewMode();

    this.primaryBusiness = true; // default COA loaded is always the primary
    this.$emit('primary', true);

    if (this.$coa.coaUUID) {
      await this.hydrateCOA(true);

      if (this.isVerifyEdit) {
        await this.hydrateVerifyInfoAddresses();
      } else {
        await this.hydrateAddresses(true);
      }
    }
  },
  methods: {
    prepareFieldErrors(resp) {
      if (!resp) return null;
      const { raw, xhr } = resp;
      if (!raw && !xhr) return null;
      const obj = xhr.fieldErrors?.reduce((accum, { field, message }) => {
        if (field === 'mobilePhone' || field === 'homePhone') {
          Object.assign(accum, { phoneNumber: { message, hasError: true } });
        } else {
          Object.assign(accum, { [field]: { hasError: true, message } });
        }
        return accum;
      }, {});
      this.improvedFieldErrors = { ...this.improvedFieldErrors, ...obj };
      return this.improvedFieldErrors;
    },
    handleChoice,
    source(src) {
      return `https://cdn.ampersend.io/${this.toggleImage(src)}.png`;
    },
    toggleImage(src) {
      return src === 'disclaimer-page-autofill' ? '26Wl9oSDj38ujOU6FhdZIE7LAV0' : '26Wl9n6yfMhCZaPOJgjywXw1FYn';
    },
    setViewMode() {
      this.isChoice = this.viewMode === 'choice';
      this.isSimplify = this.viewMode === 'simplify';
      this.isControl = this.viewMode === 'control';
      this.isVerifyInfo = this.viewMode === 'verifyInfo';
      this.isVerifyEdit = this.viewMode === 'verifyEdit';
    },
    async submitBusinessCOAForm() {
      this.buttonDisableFailSafe();
      this.toggleButtons(true);
      this.improvedFieldErrors = {};
      this.oldAddressError = '';
      this.newAddressError = '';
      this.fieldErrors = [];
      this.lazy = false;
      await this.$nextTick();
      if (this.flagRecaptchaEnabled && !this.renderV3Challenge) {
        const success = await this.recaptchaV3Validate('contact_info');
        this.recaptchaV3Success = success;
      }
      if (this.flagRecaptchaEnabled
        && !this.recaptchaV3Success
        && !this.renderV3Challenge
        && !this.recaptchaChallengeToken) {
        this.lazy = true;
        return this.recaptchaInvokeChallenge();
      }

      this.$emit('validateForm');

      if (!this.formIsValid || !this.businessAliasValid()) {
        this.scrollToFirstError();
        this.toggleButtons(false);
        return false;
      }
      const savedBusiness = await this.saveBusinessCOA();
      if (savedBusiness === false && !this.isPrimaryBusiness()) {
        this.deleteBusinessCOAByOrderUuid(this.orderUuid);
        this.editMode = false;
        return false;
      }
      this.toggleButtons(false);
      return true;
    },
    attachFieldErrors,
    scrollTo(element) {
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    },
    scrollToFirstError() {
      const errorHTML = document.querySelector('.errors');
      this.scrollTo(errorHTML);
    },
    scrollToElement(cssSelector) {
      const element = document.querySelector(cssSelector);
      this.scrollTo(element);
    },
    scrollToLastElement(cssSelector) {
      const element = document.querySelector(cssSelector)?.lastElementChild;
      this.scrollTo(element);
    },
    async populateCOA(coa) {
      if (coa.uuid) {
        this.billingAddressType = coa.billingAddressType;
        this.businessName = coa.businessName;
        this.dcoa = coa.dcoa;
        this.informedDelivery = coa.informedDelivery;
        this.forwardType = coa.forwardType;
        this.moverType = coa.moverType;
        this.startDate = coa.startDate;
        this.stopDate = coa.stopDate;

        this.$emit('updateForwardFields', coa);
      }
      if (coa.moverType === 'BUSINESS') {
        this.businessWholeCompany = coa.bizPromptEntireMoving !== null
          ? mapBooleanToRadios(coa.bizPromptEntireMoving)
          : '';
        // add logic to hydrate multipleBusiness radio once BE sends it in coa
        const { aliases } = this.flagBusinessAliasEnabled
                                    && await this.$coa.businessAliasGetAllByOrderUuid(coa.uuid);
        if (aliases?.length) {
          // open businessAlias dropdown
          this.showBusinessAlias = true;

          if (this.enhancedBusinessAliasesEnabled) {
            this.businessAliases = aliases;
          } else {
            this.businessAlias1 = 0 in aliases ? aliases[0].value : '';
            this.businessAlias2 = 1 in aliases ? aliases[1].value : '';
            this.businessAlias3 = 2 in aliases ? aliases[2].value : '';
            this.businessAlias4 = 3 in aliases ? aliases[3].value : '';
          }
        } else {
          this.resetBusinessAlias();
        }
      }
    },
    async hydrateCOA(displayPrimary) {
      this.business = await this.$coa.businessGetAll();
      if (this.business?.length >= 2) {
        this.$emit('disableNonBusinessMovers', true);
      } else {
        this.$emit('disableNonBusinessMovers', false);
      }

      if (this.isVerifyEdit) {
        const currentOrderUuid = this.$route.params.orderUuid;
        const currentBusiness = this.business?.filter((a) => a.uuid === currentOrderUuid);
        if (currentBusiness.length) {
          const { uuid } = currentBusiness[0];
          this.editCOA(uuid);
        }
      } else {
      // initially display primary
        const primary = this.business && this.business.length
                              && this.business.filter((v) => v.primaryBusiness);
        if (displayPrimary && primary.length) {
          const coa = primary[0];
          this.orderUuid = coa.uuid;
          this.selected = coa.uuid;
          this.primaryBusiness = coa.primaryBusiness;

          this.populateCOA(coa);
        }
      }
    },
    async hydrateAddresses(displayPrimary) {
      this.busAddresses = await this.$coa.busAddrGetAll();
      // display only primary address on load
      const primaryAddress = this.busAddresses?.addresses
      ?.filter((a) => a && a.orderUuid === this.orderUuid);
      if (displayPrimary && primaryAddress.length) {
        const { addresses } = primaryAddress[0];
        this.editMode = true;
        this.populateAddress(addresses);
      }
    },
    async hydrateVerifyInfoAddresses() {
      const currentOrderUuid = this.$route.params.orderUuid;
      this.busAddresses = await this.$coa.busAddrGetAll();

      const currentAddress = this.busAddresses?.addresses
      ?.filter((a) => a.orderUuid === currentOrderUuid);
      if (currentAddress.length) {
        const { addresses } = currentAddress[0];
        this.populateAddress(addresses);
      } else {
        this.$router.push({ name: 'verify-info-read' });
      }
    },
    populateAddress(addresses) {
      const [address1, address2, address3] = addresses;
      [
        address1,
        address2,
        address3,
      ]
        .filter((a) => a)
        .forEach(({ addressType, ...address }) => {
          if (addressType === 'BILLING') {
            return;
          }

          this[`${addressType.toLowerCase()}StreetAddress`] = address.streetAddress;
          this[`${addressType.toLowerCase()}City`] = address.city;
          this[`${addressType.toLowerCase()}State`] = address.state;
          this[`${addressType.toLowerCase()}ZipCode`] = address.zipCode;
          this[`${addressType.toLowerCase()}UrbanizationName`] = address.amsUrbanizationName;
          this[`${addressType.toLowerCase()}PrivateMailbox`] = address.privateMailbox;
          this[`${addressType.toLowerCase()}PoBoxNumber`] = address.poBoxNumber;
          this[`${addressType.toLowerCase()}AddressUUID`] = address?.uuid || '';

          if (addressType === 'NEW') {
            this.overrideAms = address?.overrideAms;
            this.recentConstruction = address?.recentConstruction;
          }
        });
    },
    /**
     * Select a COA to edit
     * @param {*} orderUuid
    */
    editCOA(orderUuid, scroll = true) {
      if (!this.business) {
        return;
      }

      const selectedCoa = this.business && this.business.length
                                 && this.business.filter((v) => v.uuid === orderUuid);
      if (selectedCoa?.length) {
        const coa = selectedCoa[0];
        this.orderUuid = coa.uuid;
        this.primaryBusiness = coa.primaryBusiness;
        this.editMode = true;
        this.populateCOA(coa);
      }
      if (!this.isVerifyEdit && scroll) {
        this.scrollToElement('#business-name');
      }
      // populate related addresses
      if (!this.busAddresses) {
        return;
      }

      const selectedAddress = this.busAddresses?.addresses
      ?.filter((a) => a && a.orderUuid === orderUuid);
      if (selectedAddress?.length) {
        const { addresses } = selectedAddress[0];
        this.populateAddress(addresses);
      }
    },
    /**
     * used to log high intention server event
     * @param {String} interaction - The field or button which triggered this request
     * @param {String} type - This is default to 'click' to ensure less markup in the Vue files
     */
    trackFirstEvent(interaction, type = 'click') {
      if (!this.highIntent) {
        this.$coa.eventTrack('CoaFirstInteraction', { interaction, type });
        this.$store.set('highIntent');
      }
    },
    moreAlias() {
      const bAliases = this.$refs.businessAliases;
      bAliases.moreAlias();
      bAliases.focus();
    },
    async saveBusinessCOA() {
      if (!await this.submitBusinessCOA()) {
        this.toggleButtons(false);
        return false;
      }

      await this.$nextTick();
      // If there are logged errors
      // prevent the next step
      if (this.oldAddressError
          || this.newAddressError
          || this.fieldErrors.length
           || !this.businessAliasValid()
      ) {
        this.scrollToFirstError();
        return false;
      }


      if (this.flagAmpersendEnabled && this.isPrimaryBusiness()) {
        const identityPayload = {
          mgoMoverType: this.moverType,
          mgoOldZip5: this.oldZipCode,
          mgoNewZip5: this.newZipCode,
          offersOptIn: JSON.parse(sessionStorage.getItem('mmco')) ?? this.offersChoice,
          voterregOptIn: JSON.parse(sessionStorage.getItem('mmcvr')) ?? this.autofillChoice,
        };
        if (!this.isSimplify) {
          identityPayload.mgoForwardType = this.forwardType;
        }
        this.$amp.identify(identityPayload);
      }

      if (this.forwardType === 'TEMPORARY' && this.isPrimaryBusiness()) {
        sessionStorage.setItem('informedDelivery', false);
        sessionStorage.removeItem('extension');
        sessionStorage.removeItem('extensionSelected');
      }
      sessionStorage.setItem('forwardType', this.forwardType);
      sessionStorage.setItem('moverType', this.moverType);
      sessionStorage.removeItem('newSession');

      // first COA is always taken as the primary, so once successfully save for should be cleared
      this.primaryBusiness = false;
      this.orderUuid = '';

      if (this.isVerifyEdit) {
        this.$router.push({ name: 'verify-info-read' });
        return true;
      }

      this.resetForm();
      this.editMode = false;

      // update mover and address list
      await this.hydrateCOA(false);
      await this.hydrateAddresses(false);

      // scroll to business name
      await this.$nextTick();
      this.scrollToLastElement('#businessContainer');

      return true;
    },
    addNew() {
      if (this.busAddresses.addresses.length) {
        const { addresses } = this.busAddresses.addresses[0];
        if (addresses[0] && addresses[1]) {
          this.primaryBusiness = false;
          this.orderUuid = '';
        }
      }
      this.resetForm();
      this.scrollToElement('#business-name');
    },
    async deleteBusinessCOAByOrderUuid(orderUuid) {
      const { xhr } = await this.$coa.businessDeleteCoa(orderUuid);
      this.attachFieldErrors({ xhr });
      if (xhr?.exception || this.fieldErrors?.length) {
        await this.$nextTick();
        this.scrollToFirstError();
        return false;
      }
      return true;
    },

    async deleteBusinessCOA() {
      if (!await this.deleteBusinessCOAByOrderUuid(this.orderUuid)) {
        return;
      }
      if (this.isVerifyEdit) {
        this.$router.push({ name: 'verify-info-read' });
        return;
      }
      await this.hydrateCOA(false);
      await this.hydrateAddresses(false);
      this.resetForm();
    },
    resetBusinessAlias() {
      if (this.enhancedBusinessAliasesEnabled) {
        this.businessAliases = [];
      } else {
        this.businessAlias1 = '';
        this.businessAlias2 = '';
        this.businessAlias3 = '';
        this.businessAlias4 = '';
      }
    },
    resetForm() {
      // coa
      this.dcoa = false;
      this.businessName = '';
      this.businessAlias1 = '';
      this.businessAlias2 = '';
      this.businessAlias3 = '';
      this.businessAlias4 = '';
      this.informedDelivery = false;


      // address
      if (!this.reuseOld) {
        this.oldStreetAddress = '';
        this.oldCity = '';
        this.oldState = '';
        this.oldZipCode = '';
        this.oldUrbanizationName = '';
        this.oldPoBoxNumber = '';
        this.oldAddressUUID = '';
      }

      if (!this.reuseNew) {
        this.newStreetAddress = '';
        this.newCity = '';
        this.newState = '';
        this.newZipCode = '';
        this.newUrbanizationName = '';
        this.newPoBoxNumber = '';
        this.newPrivateMailbox = '';
        this.newAddressUUID = '';
      }
      this.resetBusinessAlias();

      this.recentConstruction = false;
      this.overrideAms = false;

      this.editMode = false;
      this.lazy = true;
    },
    async getCoaOrderUuid() {
      if (!this.editMode) {
        const utmMedium = this.$route.query.utm_medium ?? '';
        const { uuid } = await this.$coa.coaCreateNew(utmMedium);
        return uuid;
      }
      return this.orderUuid;
    },
    isPrimaryBusiness() {
      return this.primaryBusiness;
    },
    async submitBusinessCOA() {
      if (this.isPrimaryBusiness) {
        sessionStorage.setItem('offersChoice', this.offersChoice);
        sessionStorage.setItem('autofillChoice', this.autofillChoice);
        sessionStorage.setItem('informedDeliveryEligible', this.informedDeliveryEligible);
        sessionStorage.setItem('startDate', this.startDate);
        sessionStorage.setItem('temp', this.forwardType === 'TEMPORARY');
        sessionStorage.setItem('optInMailReminder', this.optInMailReminder);
      }

      const {
        uuid, medUpdated, sdUpdated, xhr,
      } = this.isSimplify ? await this.$coa.businessPartialCoaUpdate({
        orderUuid: this.isPrimaryBusiness() ? this.$coa.orderUuid : await this.getCoaOrderUuid(),
        multipleAddresses: true,
        moverType: this.moverType,
        businessName: this.moverType === 'BUSINESS' ? this.businessName : null,
        famPrompt1ShareLastName: null,
        famPrompt2SameLastNameRemaining: null,
        bizPromptEntireMoving: this.businessWholeCompany
          ? this.businessWholeCompany === 'YES'
          : null,
      }) : await this.$coa.businessCoaUpdate({
        orderUuid: this.isPrimaryBusiness() ? this.$coa.orderUuid : await this.getCoaOrderUuid(),
        multipleAddresses: true,
        moverType: this.moverType,
        forwardType: this.forwardType,
        startDate: this.startDate,
        informedDelivery: this.informedDelivery,
        stopDate: this.forwardType === 'TEMPORARY' ? this.stopDate : null,
        businessName: this.moverType === 'BUSINESS' ? this.businessName : null,
        email: this.email || null,
        famPrompt1ShareLastName: this.familySameLastName
          ? this.familySameLastName === 'YES'
          : null,
        famPrompt2SameLastNameRemaining: this.familyRemaining
          ? this.familyRemaining === 'YES'
          : null,
        bizPromptEntireMoving: this.businessWholeCompany
          ? this.businessWholeCompany === 'YES'
          : null,
      });
      this.attachFieldErrors({ xhr });

      // start/stop date updated due to email domain check
      this.medUpdated = medUpdated;
      this.sdUpdated = sdUpdated;

      if (xhr?.exception || this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return false;
      }
      // should subsequest operation fail, switch to edit mode to be sure this
      // transaction doesn't get saved multiple times
      this.editMode = true;
      this.orderUuid = uuid;
      await this.$nextTick();
      this.toggleButtons(true);

      track('CoaUpdated',
        {
          dcoa: this.dcoa,
          forwardType: this.forwardType,
          informedDelivery: this.informedDelivery,
          moverType: this.moverType,
          startDate: this.startDate,
          stopDate: this.stopDate,
        });


      let businessAliasPayload = [];
      if (this.showBusinessAlias) {
        businessAliasPayload = this.enhancedBusinessAliasesEnabled
          ? this.businessAliases.map((d) => d.value).filter((v) => v)
          : [
            this.businessAlias1,
            this.businessAlias2,
            this.businessAlias3,
            this.businessAlias4,
          ].filter((v) => v);
      }

      const businessAliasHandle = this.flagBusinessAliasEnabled
          && this.moverType === 'BUSINESS' && this.$coa.businessAliasSaveByOrderUuid(uuid, businessAliasPayload);

      if (!this.isSimplify) {
        const personPayload = {
          personType: 'MOVER',
          firstName: this.moverType !== 'BUSINESS' ? this.firstName : null,
          middleName: this.moverType !== 'BUSINESS' ? this.middleName : null,
          lastName: this.moverType !== 'BUSINESS' ? this.lastName : null,
          suffix: this.moverType !== 'BUSINESS' ? this.suffix : null,
          email: this.email,
          emailConfirm: this.email,
          [`${this.phoneType.toLowerCase()}Phone`]: this.phoneNumber,
        };
        const personHandle = this.isPrimaryBusiness()
          ? this.$coa.businessPersonSave(personPayload)
          : this.$coa.personSaveByOrderUuid(uuid, personPayload);

        const [
          personResponse,
          businessAliasResponse,
        ] = await Promise.all([
          personHandle,
          businessAliasHandle,
        ]);

        this.attachFieldErrors(businessAliasResponse);
        this.attachFieldErrors(personResponse);
        this.prepareFieldErrors(personResponse);
        // also propergate exceptions received from backend rest endpoint to this component's parent
        this.$emit('fieldErrors', personResponse);
      } else {
        const [
          businessAliasResponse,
        ] = await Promise.all([
          businessAliasHandle,
        ]);
        this.attachFieldErrors(businessAliasResponse);
      }

      const payload = [{
        addressType: 'OLD',
        streetAddress: this.oldStreetAddress,
        city: this.oldCity,
        state: this.oldState,
        zipCode: this.oldZipCode,
        urbanizationName: this.oldState === 'PR'
          ? this.oldUrbanizationName
          : null,
        privateMailbox: null,
        poBoxNumber: this.oldPoBoxNumber || null,
        recentConstruction: false,
        overrideAms: false,
        moverType: this.moverType,
      }, {
        addressType: 'NEW',
        streetAddress: this.newStreetAddress,
        city: this.newCity,
        state: this.newState,
        zipCode: this.newZipCode,
        urbanizationName: this.newState === 'PR'
          ? this.newUrbanizationName
          : null,
        privateMailbox: this.newPrivateMailbox || null,
        poBoxNumber: this.newPoBoxNumber || null,
        recentConstruction: this.recentConstruction,
        overrideAms: this.overrideAms,
        moverType: this.moverType,
      }];

      if (!this.isSimplify) {
        /* eslint-disable no-param-reassign */
        payload.forEach((address) => {
          address.forwardType = this.forwardType;
        });
      }

      const addressResponses = this.isSimplify
        ? await this.$coa.businessAddressSaveSimplify(uuid, payload[0], payload[1])
        : await this.$coa.businessAddressSave(uuid, payload[0], payload[1]);

      if (this.isVerifyEdit) {
        sessionStorage.setItem('optInMailReminder', this.optInMailReminder);
        if (this.optInMailReminder) {
          this.$coa.emailReminderOptIn();
        } else {
          this.$coa.emailReminderOptOut();
        }
      }

      track(this.moverUUID ? 'PersonUpdated' : 'PersonCreated',
        {
          personType: 'MOVER',
          phoneType: this.phoneType,
        });

      if (addressResponses?.xhr?.exception) {
        if (addressResponses?.xhr?.exception === 'MilitaryZip5ForwardTypeException') {
          this.$emit('fieldErrorsMilitary', {
            field: 'forwardType', message: addressResponses?.xhr?.message || '',
          });
          this.fieldErrors = [
            ...this.fieldErrors,
            { field: 'forwardType', message: addressResponses?.xhr?.message || '' },
          ];
          this.errors.forwardType.message = addressResponses?.xhr?.message || '';
          this.errors.forwardType.hasError = true;
        } else if (addressResponses?.xhr?.exception === 'MilitaryZip5MoverTypeException') {
          this.$emit('fieldErrorsMilitary', {
            field: 'moverType', message: addressResponses?.xhr?.message || '',
          });
          this.fieldErrors = [
            ...this.fieldErrors,
            { field: 'moverType', message: addressResponses?.xhr?.message || '' },
          ];
          this.errors.moverType.message = addressResponses?.xhr?.message || '';
          this.errors.moverType.hasError = true;
        } else if (addressResponses?.xhr?.addressType === 'NEW') {
          this.newAddressError = addressResponses?.xhr?.exception || '';
          this.errors.newStreetAddress.hasError = true;
          this.attachFieldErrors(this.newAddressError, 'new');
        } else {
          this.oldAddressError = addressResponses?.xhr?.exception || '';
          this.errors.oldStreetAddress.hasError = true;
          this.attachFieldErrors(this.oldAddressError, 'old');
        }
      }
      if (addressResponses?.xhr?.exception === 'InvalidPrimaryPoBoxException' && this.errors?.newPoBoxNumber?.hasError) {
        this.errors.newStreetAddress.hasError = false;
        this.errors.newStreetAddress.message = '';
      }
      if (addressResponses?.xhr?.fieldErrors && /new/g.test(addressResponses?.xhr?.fieldErrors[0].field)) {
        this.newAddressError = addressResponses?.xhr?.fieldErrors[0];
        this.attachFieldErrors(this.newAddressError, 'new');
      }
      if (addressResponses?.xhr?.fieldErrors && /old/g.test(addressResponses?.xhr?.fieldErrors[0].field)) {
        this.oldAddressError = addressResponses?.xhr?.fieldErrors[0];
        this.attachFieldErrors(this.oldAddressError, 'old');
      }
      if (addressResponses?.xhr?.fieldErrors) {
        // eslint-disable-next-line no-unused-expressions
        addressResponses?.xhr?.fieldErrors.forEach((fieldError) => {
          const fieldNameError = fieldError.field?.split('.')[1].charAt(0).toUpperCase() + fieldError.field?.split('.')[1].slice(1);
          const oldAddressField = `old${fieldNameError}`;
          const newAddressField = `new${fieldNameError}`;
          if (fieldError.field.indexOf('newAddress') === 0) {
            this.errors[newAddressField].message = fieldError.message;
            this.errors[newAddressField].hasError = true;
          } else {
            this.errors[oldAddressField].message = fieldError.message;
            this.errors[oldAddressField].hasError = true;
          }
        });
      }
      if (this.oldAddressError === 'OldAndNewAddressMatchException'
         || this.newAddressError === 'OldAndNewAddressMatchException') {
        this.newAddressError = 'OldAndNewAddressMatchException';
        this.oldAddressError = 'OldAndNewAddressMatchException';
      }

      // log the defaultExceptions for old/new address
      if (!addressResponses?.xhr?.fieldErrors) {
        if (this.oldAddressError) {
          this.defaultException(this.oldAddressError);
        }
        if (this.newAddressError) {
          this.defaultException(this.newAddressError);
        }
      }

      const errorPageRedirect = this.evaluateErrorRedirect(addressResponses);
      if (errorPageRedirect) {
        this.$store.commit('DISABLE_LOADER');
        this.$router.push({ name: errorPageRedirect });
        return false;
      }

      if (!addressResponses?.NEW?.uuid) {
        this.newConstructionEvaluateAddress(addressResponses);
      }

      if (this.newConstructionAttempts > 0) {
        this.$store.commit('DISABLE_LOADER');
        return false;
      }

      // coaCheckHandle after the old/newAddress response
      const coaCheckHandle = await this.$coa.coaCheckByUUID(uuid);

      // Throw coaCheckModal if 'CoaCheckException'
      if (coaCheckHandle?.error) {
        const { exception } = coaCheckHandle.xhr;
        if (exception === 'CoaCheckException') {
          this.coaCheckModal = true;
          this.editMode = this.isPrimaryBusiness();

          this.defaultException('DUPCOAEXCEPTION');
          this.$store.commit('DISABLE_LOADER');
          return false;
        }
      }

      // log the amsStatusCodes on 200 responses for old/new addresses
      if (addressResponses?.addressType
         && this.isAmsMatch(addressResponses?.amsStatusCode)) {
        this.defaultException(addressResponses.amsStatusCode);
      }

      if (this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return false;
      }
      return true;
    },
    async isPrimaryDuplicate() {
      const coaCheckHandle = await this.$coa.coaCheckByUUID(this.coaUUID);

      // Throw coaCheckModal if 'CoaCheckException'
      if (coaCheckHandle?.error) {
        const { exception } = coaCheckHandle.xhr;
        if (exception === 'CoaCheckException') {
          this.editCOA(this.$coa.orderUuid);
          this.coaCheckModal = true;
          this.defaultException('DUPCOAEXCEPTION');
          this.$store.commit('DISABLE_LOADER');
          return true;
        }
      }
      return false;
    },
    businessAliasValid() {
      const isValid = this.flagBusinessAliasEnabled && this.showBusinessAlias
       && this.moverType === 'BUSINESS' && this.enhancedBusinessAliasesEnabled
        ? this.$refs.businessAliases?.isValid() : true;
      return isValid;
    },
    buttonDisableFailSafe() {
      // will enable buttons after 10 sec if needed
      setTimeout(() => {
        this.toggleButtons(false);
      }, 10000);
    },
    toggleButtons(toggle) {
      const save = document.getElementById('biz-save');
      const update = document.getElementById('biz-update');
      const add = document.getElementById('biz-add');

      if (save) save.disabled = toggle;
      if (update) update.disabled = toggle;
      if (add) add.disabled = toggle;
    },
    async validateSubmitResponse() {
      // Save on first record to prompt
      // new construction for primary
      let primary = false;
      if (this.business.length < 1) {
        primary = true;
        const valid = await this.submitBusinessCOAForm();
        if (valid === false) { return false; }
      }
      // If there are logged errors
      // prevent the next step
      if (this.oldAddressError
        || this.newAddressError
        || this.fieldErrors.length) {
        this.$store.commit('DISABLE_LOADER');
        this.scrollToFirstError();
        return false;
      }

      // save primary business
      // prevent next step if primary has error
      if (this.business.length >= 1 && !primary) {
        this.$store.commit('ENABLE_LOADER');
        this.editCOA(this.$coa.orderUuid, false);
        const valid = await this.submitBusinessCOAForm();
        if (valid === false) {
          this.$store.commit('DISABLE_LOADER');
          return false;
        }
      }
      return true;
    },
    /**
     * @param {*} query query object for the Vue Router
     */
    async nextPage(query = {}) {
      await this.$nextTick();
      if (await this.isPrimaryDuplicate()) {
        return;
      }

      // add start date updated to query params
      if (this.medUpdated) {
        Object.assign(query, { medUpdated: this.medUpdated });
      }

      // add stop date updated to query params
      if (this.sdUpdated) {
        Object.assign(query, { sdUpdated: this.sdUpdated });
      }
      if (this.forwardType === 'TEMPORARY' && this.isPrimaryBusiness()) {
        sessionStorage.setItem('informedDelivery', false);
        sessionStorage.removeItem('extension');
        sessionStorage.removeItem('extensionSelected');
      }
      sessionStorage.setItem('forwardType', this.forwardType);
      sessionStorage.setItem('moverType', this.moverType);
      sessionStorage.removeItem('newSession');
      this.$router.push({ name: this.handleNavigation, query });
    },
    async nextRoute() {
      return this.nextPage({
        ...this.$route.query,
        ...(this.medUpdated && { medUpdated: this.medUpdated }),
        ...(this.sdUpdated && { sdUpdated: this.sdUpdated }),
      });
    },
  },
};
